import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 15000, // 15 seconds timeout
    // Add retry logic for network errors
    retry: 3,
    retryDelay: (retryCount) => {
        return retryCount * 1000; // time interval between retries
    }
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor for retry logic on network errors
api.interceptors.response.use(undefined, (err) => {
    const { config, message } = err;
    if (!config || !config.retry) return Promise.reject(err);

    // Set the variable for keeping track of the retry count
    config.__retryCount = config.__retryCount || 0;

    // Check if we've maxed out the total number of retries
    if (config.__retryCount >= config.retry) {
        return Promise.reject(err);
    }

    // Check if this is a network error
    if (message && (message.includes('Network Error') || message.includes('timeout'))) {
        // Increase the retry count
        config.__retryCount += 1;

        // Create new promise to handle exponential backoff
        const backoff = new Promise((resolve) => {
            setTimeout(() => {
                console.log(`Retrying request (${config.__retryCount}/${config.retry})`);
                resolve();
            }, config.retryDelay(config.__retryCount));
        });

        // Return the promise in which recalls axios to retry the request
        return backoff.then(() => api(config));
    }

    return Promise.reject(err);
});

export const login = async (email, password) => {
    try {
        const response = await api.post('/auth/login', { email, password });
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
        }
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: error.message };
    }
};

export const register = async (userData) => {
    try {
        const response = await api.post('/auth/register', userData);
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
        }
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: error.message };
    }
};

export const getCurrentUser = async () => {
    try {
        const response = await api.get('/auth/me');
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: error.message };
    }
};

export const updateProfile = async (profileData) => {
    try {
        const response = await api.put('/auth/profile', profileData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Profile update failed' };
    }
};

export const logout = async () => {
    try {
        // Close out the active session record server-side before the token
        // that authorizes this request is cleared
        await api.get('/auth/logout');
    } catch (error) {
        console.error('Logout request failed', error);
    } finally {
        localStorage.removeItem('token');
    }
};

export const googleAuth = async (credential) => {
    try {
        const response = await api.post('/auth/google', { credential });
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
        }
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: error.message };
    }
};


// Search API
export const searchProducts = async (query, page = 1, limit = 20) => {
    try {
        const response = await api.get('/search', {
            params: {
                query,
                page,
                limit
            }
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || { message: 'Search failed' };
    }
};

// Record a completed product search (call once per search results page load)
export const trackProductSearch = async (query) => {
    try {
        const response = await api.post('/search/track', { query });
        return response.data;
    } catch (error) {
        // Non-critical: don't disrupt the customer's search experience if tracking fails
        console.error('Failed to track search:', error);
    }
};

// SEO Settings API
export const getSeoSettings = async () => {
    try {
        const response = await api.get('/settings/seo');
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to fetch SEO settings' };
    }
};

export const updateSeoSettings = async (seoData) => {
    try {
        const response = await api.post('/admin/settings/seo', seoData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to update SEO settings' };
    }
};

// Sale Management API
export const updateProductSale = async (productId, saleData) => {
    try {
        const response = await api.patch(`/products/${productId}/sale`, saleData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to update product sale' };
    }
};

export const removeProductSale = async (productId) => {
    try {
        const response = await api.delete(`/products/${productId}/sale`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to remove product sale' };
    }
};

export const getActiveSales = async (filters = {}) => {
    try {
        const response = await api.get('/products/sales/active', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to fetch active sales' };
    }
};

export const searchProductsForSales = async (searchQuery, limit = 8) => {
    try {
        const response = await api.get('/products/admin/search-for-sales', {
            params: { search: searchQuery, limit }
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to search products' };
    }
};

// Near Expiry API functions
export const updateProductNearExpiry = async (productId, nearExpiryData) => {
    try {
        const response = await api.patch(`/products/${productId}/near-expiry`, nearExpiryData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to update near expiry' };
    }
};

export const removeProductNearExpiry = async (productId) => {
    try {
        const response = await api.delete(`/products/${productId}/near-expiry`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to remove near expiry' };
    }
};

export const getActiveNearExpiry = async (filters = {}) => {
    try {
        const response = await api.get('/products/near-expiry/active', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to fetch active near expiry' };
    }
};

export const searchProductsForNearExpiry = async (query) => {
    try {
        const response = await api.get('/products/near-expiry/search', { params: { q: query } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to search products for near expiry' };
    }
};

// Most Searched Keywords API (admin)
export const getMostSearchedKeywords = async (params = {}) => {
    try {
        const response = await api.get('/search/admin/most-searched', { params });
        return response.data;
    } catch (error) {
        throw error.response?.data || { error: 'Failed to fetch most searched keywords' };
    }
};

export default api;